<script lang="ts">
    import { getImageSizeUrl } from "@/utils/getImageSizeUrl";
    import ReviewEntity from "@apps/reviews/entities/ReviewEntity";
    import CN from "classnames";

    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import Image from "@components/Image/Image.svelte";
    import EmblaSlider from "@components/Slider/EmblaSlider.svelte";
    import type { EmblaSliderApi } from "@components/Slider/types";

    import ImageSliderArrows from "./ImageSliderArrows.svelte";

    export let images: ReviewEntity["images"];
    export let activeImageIndex: number | undefined;
    export let reviewTitle: string | null;

    let emblaApi: EmblaSliderApi | null = null;
    let activeSlideIndex = 0;

    const updateNavState = () => {
        if (!emblaApi) return;
        activeSlideIndex = emblaApi.selectedScrollSnap();
    };

    const handleGoTo = (idx: number) => {
        emblaApi?.scrollTo(idx);
    };

    const handleNext = () => {
        emblaApi?.scrollNext();
    };

    const handlePrev = () => {
        emblaApi?.scrollPrev();
    };

    const handleEmblaSelect = () => {
        updateNavState();
    };

    const handleEmblaReady = () => {
        if (!emblaApi) return;
        // Ensure initial state
        updateNavState();
        // Scroll to initial active image if specified and different
        if (typeof activeImageIndex === "number") {
            const current = emblaApi.selectedScrollSnap();
            if (activeImageIndex !== current) {
                emblaApi.scrollTo(activeImageIndex);
            }
        }
    };

    $: {
        if (emblaApi && typeof activeImageIndex === "number") {
            const current = emblaApi.selectedScrollSnap();
            if (activeImageIndex !== current) {
                emblaApi.scrollTo(activeImageIndex);
            }
        }
    }
</script>

<div class="gw-rv-gallery-image-slider">
    <div class="gw-rv-gallery-image-slider__slider">
        <EmblaSlider
            setApi={(api) => (emblaApi = api)}
            perPage={1}
            duration={10}
            controls={true}
            on:select={handleEmblaSelect}
            on:ready={handleEmblaReady}
        >
            <svelte:fragment slot="slides">
                {#each images as image (image.id)}
                    <div class="gw-rv-gallery-image-slider__img-wrapper">
                        <Image
                            wrapperClassName="gw-rv-gallery-image-slider__img"
                            backdropClassName="gw-rv-gallery-image-slider__img-backdrop"
                            src={getImageSizeUrl(image.sizes, "md") || ""}
                            objectFit="contain"
                            alt="review image"
                        />
                    </div>
                {/each}
            </svelte:fragment>
            <svelte:fragment slot="arrows" let:hasPrev let:hasNext>
                <ImageSliderArrows
                    on:nextSlide={handleNext}
                    on:prevSlide={handlePrev}
                    {hasNext}
                    {hasPrev}
                />
            </svelte:fragment>
        </EmblaSlider>
    </div>

    <div class="gw-rv-gallery-image-slider__hooper">
        <ul
            class="gw-rv-gallery-image-slider__hooper-list gw-list-reset-default-styles"
        >
            {#each images as image, idx (image.id)}
                <li class="gw-rv-gallery-image-slider__hooper-item">
                    <BaseButton
                        class={CN("gw-rv-gallery-image-slider__hooper-btn", {
                            "gw-rv-gallery-image-slider__hooper-btn--selected":
                                idx === activeSlideIndex,
                        })}
                        on:click={() => handleGoTo(idx)}
                        ariaLabel={`Review image  ${idx + 1}`}
                    >
                        <Image
                            ratio={64 / 64}
                            src={getImageSizeUrl(image.sizes, "xs") || ""}
                            themeColor
                            objectFit="cover"
                            alt={`${reviewTitle || "Review image"} ${idx + 1}`}
                            imgClassName="gw-rv-gallery-image-slider__hooper-img"
                        />
                    </BaseButton>
                </li>
            {/each}
        </ul>
    </div>
</div>

<style lang="postcss">
    .gw-rv-gallery-image-slider {
        height: 100%;
        position: relative;
        --gw-image-backdrop-color: rgba(46, 46, 46, 1);

        @media screen and (max-width: 600px) {
            --gw-image-backdrop-color: transparent;
        }

        &__slider {
            height: 100%;
            background-color: var(--gw-image-backdrop-color);
        }

        &__img-wrapper {
            height: 580px;

            @media screen and (max-width: 1000px) {
                height: 300px;
            }

            @media screen and (max-width: 600px) {
                height: 335px;
            }
        }

        &__hooper {
            width: 100%;
            display: flex;
            justify-content: center;
            position: absolute;
            bottom: 14px;

            @media screen and (max-width: 1000px) {
                bottom: 12px;
            }

            @media screen and (max-width: 600px) {
                position: static;
                margin-top: 10px;
                justify-content: flex-start;
            }
        }

        &__hooper-list {
            display: flex;
            justify-content: space-between;
            gap: 0.5em;
        }

        &__hooper-item {
            width: 70px;
            height: 70px;
            display: flex;
            justify-content: center;
            align-items: center;

            @media screen and (max-width: 1000px) {
                width: 60px;
                height: 60px;
            }

            @media screen and (max-width: 600px) {
                width: 50px;
                height: 50px;
            }
        }

        :global(&__hooper-img) {
            border-radius: 2px;
        }
    }

    :global(.gw-btn.gw-rv-gallery-image-slider__hooper-btn) {
        box-sizing: content-box;
        display: block;
        width: 64px;
        height: 64px;
        padding: 1px;

        @media screen and (max-width: 1000px) {
            width: 56px;
            height: 56px;
        }

        @media screen and (max-width: 600px) {
            box-sizing: border-box;
            width: 50px;
            height: 50px;
        }
    }

    :global(.gw-btn.gw-rv-gallery-image-slider__hooper-btn--selected) {
        border: 2px solid white;
        border-radius: 4px;

        @media screen and (max-width: 600px) {
            border: 1px solid black;
            border-radius: 0;
        }
    }
</style>
